/**
 * Small building blocks of the template detail tabs: fact rows, token lists, status badge, formatters. Demo page:
 * /profile (key/value rows inside account cards).
 */
import React from 'react';
import { Badge, Chip, type BadgeVariant } from '../../../components/ui/index.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';

export function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'Not scored';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Not scored';
  return `${Math.round(n * 100)}%`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return date.toLocaleDateString();
}

/** A label with a value on the right, for the fact cards. */
export const FactRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b py-2.5 text-sm first:pt-0 last:border-0 last:pb-0">
    <dt className="text-muted">{label}</dt>
    <dd className="min-w-0 text-right wrap-break-word text-heading">{value}</dd>
  </div>
);

export const FactList: React.FC<{ children: React.ReactNode }> = ({ children }) => <dl>{children}</dl>;

export const TokenList: React.FC<{ label: string; values: readonly string[]; empty: string }> = ({
  label,
  values,
  empty,
}) => (
  <div>
    <div className="mb-2 text-xs font-medium text-muted">{label}</div>
    {values.length === 0 ? (
      <p className="text-sm text-muted">{empty}</p>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {values.map((value) => (
          <Chip key={value}>{value}</Chip>
        ))}
      </div>
    )}
  </div>
);

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  supported: 'success',
  active: 'success',
  review_required: 'warning',
  draft: 'warning',
  not_recommended: 'danger',
  retired: 'neutral',
};

/** Applicability and version statuses as one badge vocabulary. */
export const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge variant={STATUS_VARIANT[status] ?? 'neutral'} dot>
    {sentenceCase(status)}
  </Badge>
);
